import type { NavigateToPath } from '@/lib/desktop-app-navigation';
import type { REALTIME, REALTIME_KEYS } from '@/lib/realtime/types';
import { notification, platform, powerMonitor } from '@todesktop/client-core';

import { getInternalNotificationPath } from './notification-path';
import { prepareQuickAction } from './prepare-quick-action';
import { forgetToast, rememberToast, trackToast } from './pull-request-toasts';

import { clientLogger } from '@/lib/client-logger';
import { openInDesktopAppWindow } from '@/lib/desktop-app-navigation';
import { isDevelopment } from '@/lib/env';
import { isPrV2Route } from '@/lib/pr-v2/route-utils';

export type AppNotification =
  (typeof REALTIME)[typeof REALTIME_KEYS.DESKTOP_NOTIFICATION]['payloadType']['payload'];

/** What a toast shows. Toasts the app raises itself, such as a quick action's result, have no id. */
export type ToastContent = Omit<AppNotification, 'id' | 'sentAt'> &
  Partial<Pick<AppNotification, 'id'>>;

/** The second button of a notification with one; the first, `Open`, is the default. */
const QUICK_ACTION_BUTTON_INDEX = 1;

/**
 * The person is already looking at what the notification is about: the focused window shows the
 * pull request or /code session its link opens. Other pages, such as the inbox, do not show the
 * update itself, so a toast linking there still shows.
 */
function isShowingTarget(targetPath: string | null): boolean {
  if (!targetPath || !document.hasFocus()) {
    return false;
  }
  const targetPage = targetPath.replace(/#.*$/, '');
  const showsSubject = isPrV2Route(targetPage) || targetPage.startsWith('/code/');
  return showsSubject && `${window.location.pathname}${window.location.search}` === targetPage;
}

/**
 * Electron emits `action` as `(details, actionIndex)` and `reply` as `(details, text)`, where
 * `details` carries the same value, while client-core 1.20 still types the older single-argument
 * form. Read the value from whichever form ToDesktop forwards.
 */
function readActionIndex(args: unknown[]): number | null {
  for (const arg of args) {
    const value = typeof arg === 'object' && arg !== null ? Reflect.get(arg, 'actionIndex') : arg;
    if (typeof value === 'number') {
      return value;
    }
  }
  return null;
}

function readReplyText(args: unknown[]): string | null {
  for (const arg of args) {
    const value = typeof arg === 'object' && arg !== null ? Reflect.get(arg, 'reply') : arg;
    if (typeof value === 'string') {
      return value;
    }
  }
  return null;
}

/** Tells the server this app showed a published toast, so its Slack DM can be held. */
function acknowledgeToast(id: string): void {
  // At the machine means the computer had input within the last minute.
  powerMonitor
    .getSystemIdleState(60)
    .then(async (idleState) => {
      const response = await fetch('/api/desktop/notifications/shown', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, active: idleState === 'active' }),
      });
      if (!response.ok) {
        throw new Error(`Failed to acknowledge a desktop notification (${response.status})`);
      }
    })
    .catch((error: unknown) => {
      clientLogger.error('Failed to acknowledge a desktop notification', error);
    });
}

/**
 * Creates and shows a web notification with the given title and body.
 * Returns true if the notification was shown successfully.
 */
export async function sendElectronOrWebNotification(
  appNotification: ToastContent,
  navigate: NavigateToPath,
): Promise<boolean> {
  const { id, title, link: actionUrl, desktopOnly } = appNotification;
  try {
    const targetPath = getInternalNotificationPath(actionUrl);
    if (isShowingTarget(targetPath)) {
      return true;
    }

    // Try sending to the desktop app first. If unsuccessful, send a web notification
    if (platform.todesktop.isDesktopApp()) {
      // Tracked before the awaits below, so opening or merging the pull request meanwhile takes
      // it back too.
      const toast = targetPath ? trackToast(targetPath) : null;
      try {
        const quickAction = await prepareQuickAction(appNotification, {
          navigate,
          notify: (followUp) => sendElectronOrWebNotification(followUp, navigate),
        });
        // Create a desktop notification using ToDesktop API
        const notificationRef = await notification.create({
          title: isDevelopment() ? `[DEV] ${title}` : title,
          body: quickAction.body,
          silent: false,
          ...quickAction.options,
        });

        const openTarget = () => {
          // The app is already running, so navigate its current window directly. Re-entering
          // through cubic:// makes ToDesktop resolve the path relative to the /inbox start URL.
          // A user clicking a cubic toast should reveal cubic even when its target cannot be
          // navigated, so a null target still reveals the window.
          void openInDesktopAppWindow(targetPath, navigate).catch((error) => {
            clientLogger.error('Failed to reveal desktop app after notification click', error);
          });
        };

        // Set up click handler for the notification
        await notification.on(
          'click',
          () => {
            console.log('Desktop notification clicked, action url:', actionUrl);
            openTarget();
          },
          { ref: notificationRef },
        );

        const { onButton, onReply } = quickAction;
        if (onButton) {
          await notification.on(
            'action',
            (...args: unknown[]) => {
              if (readActionIndex(args) === QUICK_ACTION_BUTTON_INDEX) {
                onButton();
              } else {
                openTarget();
              }
            },
            { ref: notificationRef },
          );
        }

        if (onReply) {
          await notification.on(
            'reply',
            (...args: unknown[]) => {
              const reply = readReplyText(args)?.trim();
              if (reply) {
                onReply(reply);
              }
            },
            { ref: notificationRef },
          );
        }

        if (toast?.outdated) {
          return true;
        }
        // Show the notification
        await notification.show({ ref: notificationRef });
        if (toast) {
          rememberToast(toast, notificationRef);
        }
        if (id) {
          acknowledgeToast(id);
        }
        console.log('Desktop notification sent successfully');
        return true;
      } catch (desktopError) {
        if (toast) {
          forgetToast(toast);
        }
        console.log('Desktop notification failed, falling back to web notification:', desktopError);
      }
    } else {
      console.log('Not in desktop app, using web notification');
    }

    if (desktopOnly) {
      return false;
    }

    const webNotification = new Notification(isDevelopment() ? `[DEV] ${title}` : title, {
      body: appNotification.body,
      data: { actionUrl },
      requireInteraction: true,
    });

    // Handle clicks from browser notifications in the current web app.
    webNotification.onclick = () => {
      console.log('Notification clicked, action url:', actionUrl);
      if (targetPath) {
        window.focus();
        navigate(targetPath);
        console.log('Navigated to action url');
      }
    };

    console.log('Web notification created successfully');
    return true;
  } catch (error) {
    console.error('Failed to send Web notification:', error);
    return false;
  }
}
